import { Routes, Route, Link, useLocation } from 'react-router-dom'
import { useTheme } from './context/ThemeContext'
import { LogoutButton } from './components/molecules/LogoutButton'
import LoginPage from './components/LoginPage'
import Dashboard from './components/Dashboard'
import AppsPage from './components/pages/AppsPage'
import AppFeedbackPage from './components/pages/AppFeedbackPage'
import FeedbackManagementPage from './components/pages/FeedbackManagementPage'
import AdminPage from './components/pages/AdminPage'
import SettingsPage from './components/pages/SettingsPage'
import { LogoWithStatus } from './components/molecules/LogoWithStatus'
import { useUnifiedAuth } from './auth/AuthProvider'
import { showAdminFeatures } from './config/env'

function App() {
  const { theme, setTheme } = useTheme()
  const { isAuthenticated, isLoading, user } = useUnifiedAuth()
  const location = useLocation()

  const toggleTheme = () => {
    if (theme === 'light') {
      setTheme('dark')
    } else if (theme === 'dark') {
      setTheme('system')
    } else {
      setTheme('light')
    }
  }

  // Show loading state while Auth0 is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white text-gray-950 transition-theme dark:bg-gray-950 dark:text-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-body-sm text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  // Show login page when not authenticated
  if (!isAuthenticated) {
    return <LoginPage />
  }

  // Show main admin interface when authenticated
  return (
    <div className="min-h-screen bg-white text-gray-950 transition-theme dark:bg-gray-950 dark:text-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {/* Ping Logo with Status */}
            <LogoWithStatus 
              status="operational" 
              size="medium-large" 
            />
            
            {/* Title */}
            <div>
              <h1 className="text-h2 text-gray-950 dark:text-gray-50">
                Ping Pong Feedback
              </h1>
              <p className="text-subtitle text-gray-600 dark:text-gray-400">
                User Feedback Management
              </p>
            </div>
          </div>
          
          {/* Header Actions */}
          <div className="flex items-center gap-2">
            {/* User Info */}
            <div className="hidden sm:block text-right mr-2">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Welcome back{user?.name && `, ${user.name}`}
              </div>
              {user?.email && (
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  {user.email}
                </div>
              )}
            </div>
            
            <LogoutButton />
            
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="btn btn-ghost touch-target min-w-24 px-4"
              data-testid="theme-toggle"
              aria-label={`Switch to ${theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'} theme`}
            >
              {theme === 'light' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
              {theme === 'dark' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
              {theme === 'system' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              )}
              <span className="ml-2 text-sm hidden sm:inline">
                {theme === 'light' ? 'Light' : theme === 'dark' ? 'Dark' : 'System'}
              </span>
            </button>
          </div>
        </header>

        {/* Navigation */}
        <nav className="mb-8">
          <div className="flex flex-wrap gap-1 p-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <Link 
              to="/" 
              className={`nav-link ${location.pathname === '/' ? 'nav-link-active' : ''}`}
              data-testid="nav-dashboard"
            >
              Dashboard
            </Link>
            <Link 
              to="/feedback" 
              className={`nav-link ${location.pathname === '/feedback' ? 'nav-link-active' : ''}`}
              data-testid="nav-feedback"
            >
              Feedback
            </Link>
            <Link 
              to="/apps" 
              className={`nav-link ${location.pathname.startsWith('/apps') ? 'nav-link-active' : ''}`}
              data-testid="nav-apps"
            >
              Apps
            </Link>
            <Link 
              to="/analytics" 
              className={`nav-link ${location.pathname === '/analytics' ? 'nav-link-active' : ''}`}
              data-testid="nav-analytics"
            >
              Analytics
            </Link>
            <Link 
              to="/users" 
              className={`nav-link ${location.pathname === '/users' ? 'nav-link-active' : ''}`}
              data-testid="nav-users"
            >
              Users
            </Link>
            {showAdminFeatures && (
              <>
                <Link
                  to="/settings"
                  className={`nav-link ${location.pathname === '/settings' ? 'nav-link-active' : ''}`}
                  data-testid="nav-settings"
                >
                  Settings
                </Link>
                <Link
                  to="/admin"
                  className={`nav-link ${location.pathname === '/admin' ? 'nav-link-active' : ''}`}
                  data-testid="nav-admin"
                >
                  Admin
                </Link>
              </>
            )}
          </div>
        </nav>

        {/* Main Content */}
        <main>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/callback" element={<Dashboard />} />
            <Route path="/feedback" element={<FeedbackManagementPage />} />
            <Route path="/apps" element={<AppsPage />} />
            <Route path="/apps/:appId" element={<AppFeedbackPage />} />
            <Route path="/analytics" element={<div className="card p-6"><h2 className="text-h3 mb-4">Analytics & Reports</h2><p>Feedback trends and application insights coming soon...</p></div>} />
            <Route path="/users" element={<div className="card p-6"><h2 className="text-h3 mb-4">User Assignment</h2><p>Assign feedback to team members for review coming soon...</p></div>} />
            {showAdminFeatures && (
              <>
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/admin" element={<AdminPage />} />
              </>
            )}
          </Routes>
        </main>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-muted-foreground">
          <p>Ping Pong Feedback v1.0.0 - (C) Crossmoore AI Limited 2025</p>
        </footer>
      </div>
    </div>
  )
}

export default App 